import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Lock, 
  Eye, 
  EyeOff, 
  CheckCircle, 
  Loader,
  AlertCircle,
  Shield
} from 'lucide-react';
import { resetPassword } from "../api/api";
import config from '../utils/config';

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  
  const { token } = useParams();
  const navigate = useNavigate();

  // Password validation
  const validatePassword = (password) => {
    const errors = {};
    
    if (password.length < 6) {
      errors.length = 'Password must be at least 6 characters long';
    }
    
    if (!/(?=.*[a-z])/.test(password)) {
      errors.lowercase = 'Password must contain at least one lowercase letter';
    }
    
    if (!/(?=.*[A-Z])/.test(password)) {
      errors.uppercase = 'Password must contain at least one uppercase letter';
    }
    
    if (!/(?=.*\d)/.test(password)) {
      errors.number = 'Password must contain at least one number';
    }
    
    return errors;
  };

  const handlePasswordChange = (password) => {
    setNewPassword(password);
    setValidationErrors(validatePassword(password));
  };

  const handleSubmit = async () => {
    // Validation
    if (!newPassword.trim()) {
      setMessage('Please enter a new password');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    if (!confirmPassword.trim()) {
      setMessage('Please confirm your password');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    // Check for validation errors
    const errors = validatePassword(newPassword);
    if (Object.keys(errors).length > 0) {
      setMessage('Please fix the password requirements');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    setLoading(true);
    setMessage('');
    
    try {

      const result = await resetPassword(token, newPassword, confirmPassword);

      if (result.responsecode === 200) {
        setSuccess(true);
        setMessage('Password reset successfully! Redirecting to login...');
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        console.log(result || 'Failed to reset password. Please try again.');
        setTimeout(() => setMessage(''), 5000);
      }
      
    } catch (error) {
      console.log('Reset password error:', error);
      setMessage('Failed to reset password. Please try again.');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  // Password strength indicator
  const getPasswordStrength = () => {
    const errors = Object.keys(validationErrors).length;
    if (newPassword.length === 0) return { strength: 0, label: '', color: '' };
    if (errors === 0) return { strength: 100, label: 'Strong', color: 'bg-green-500' };
    if (errors <= 2) return { strength: 60, label: 'Medium', color: 'bg-yellow-500' };
    return { strength: 30, label: 'Weak', color: 'bg-red-500' };
  };

  const passwordStrength = getPasswordStrength();

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-6 sm:px-6 lg:px-8 bg-gray-50">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mb-4">
              <img 
                src={`${config.IMG_URL}/tuly.png`}
                alt="Tuly" 
                className="h-16 w-auto mx-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='200' height='50' viewBox='0 0 200 50'%3e%3ctext x='0' y='40' font-family='Arial' font-size='40' font-weight='bold' fill='blue'%3eTuly%3c/text%3e%3c/svg%3e";
                }}
              />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Reset Your Password</h1>
            <p className="text-gray-600 mt-2">Enter your new password below</p>
          </div>

          {success ? (
            /* Success State */
            <div className="text-center">
              <div className="mb-6">
                <CheckCircle size={64} className="text-green-500 mx-auto" />
              </div>
              <h2 className="text-xl font-semibold text-green-800 mb-2">Password Reset Successfully!</h2>
              <p className="text-gray-600 mb-4">Your password has been updated. You will be redirected to the login page shortly.</p>
              <div className="flex items-center justify-center">
                <Loader size={20} className="animate-spin mr-2" />
                <span className="text-sm text-gray-500">Redirecting...</span>
              </div>
            </div>
          ) : (
            /* Reset Form */
            <div className="space-y-6">
              {/* New Password Field */}
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock size={18} className="text-gray-400" />
                  </div>
                  <input
                    id="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="w-full pl-10 pr-12 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter your new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showNewPassword ? (
                      <EyeOff size={18} className="text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye size={18} className="text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
                
                {/* Password Strength Indicator */}
                {newPassword && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-gray-500">Password strength</span>
                      <span className={`font-medium ${
                        passwordStrength.strength === 100 ? 'text-green-600' :
                        passwordStrength.strength >= 60 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {passwordStrength.label}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                        style={{ width: `${passwordStrength.strength}%` }}
                      ></div>
                    </div>
                  </div>
                )}
                
                {/* Password Requirements */}
                {newPassword && Object.keys(validationErrors).length > 0 && (
                  <div className="mt-2 space-y-1">
                    {Object.entries(validationErrors).map(([key, error]) => (
                      <div key={key} className="flex items-center text-xs text-red-600">
                        <AlertCircle size={12} className="mr-1" />
                        {error}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Shield size={18} className="text-gray-400" />
                  </div>
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="w-full pl-10 pr-12 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Confirm your new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={18} className="text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye size={18} className="text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
                
                {/* Password Match Indicator */}
                {confirmPassword && (
                  <div className="mt-1">
                    {newPassword === confirmPassword ? (
                      <div className="flex items-center text-xs text-green-600">
                        <CheckCircle size={12} className="mr-1" />
                        Passwords match
                      </div>
                    ) : (
                      <div className="flex items-center text-xs text-red-600">
                        <AlertCircle size={12} className="mr-1" />
                        Passwords do not match
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Error Message */}
              {message && !success && (
                <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center">
                  <AlertCircle size={18} className="mr-2" />
                  {message}
                </div>
              )}

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={loading || Object.keys(validationErrors).length > 0}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg px-5 py-3 text-center transition-colors flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader size={20} className="animate-spin mr-2" />
                    Resetting Password...
                  </>
                ) : (
                  "Reset Password"
                )}
              </button>

              {/* Back to Login */}
              <div className="text-center">
                <button
                  onClick={() => navigate('/login')}
                  className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                >
                  Back to Login
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;